import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageComponent } from '@/components/Image';
import { useMedia } from '@/hooks';
import { useButtonAction, useIsSticky, useWorkSans } from '@/modules/promo';
import { Button, Logo } from '../../components';
import { DashedCopyCode } from '../../components/DashedCopyCode';
import { Loader } from '../../components/Loader';
import { HeroCard } from './components/HeroCard';
import { JackpotCard } from './components/JackpotCard';
import { TopGamesCard } from './components/TopGamesCard';
import { VIPCard } from './components/VIPCard';
import { WelcomeOfferCard } from './components/WelcomeOfferCard';
import { getCardPosition, getCardStyle } from './helpers';

import { ScCoverflowSliderTemplate } from './styled';

type CardName = 'hero' | 'welcome' | 'vip' | 'games' | 'jackpot';
type CardFactory = (isActive: boolean) => JSX.Element;

export type HeroCardBenefit = {
  image: string;
  translationKey: string;
};

interface CoverflowSliderTemplateProps {
  backgroundImage: string;
  translation: string;
  heroImage: string;
  code?: string;
  ctaButtonText?: string;
  games: string[];
  JackpotBgImage: string;
  cardOrder?: CardName[];
  initialIndex?: number;
  heroCardCardBenefits?: HeroCardBenefit[];
  staticTopGames?: boolean;
  validDate?: string;
  getCurrentIndex?: (index: number) => void;
  fsCount?: number | null;
}

export const CoverflowSliderTemplate = ({
  backgroundImage,
  heroImage,
  translation,
  code,
  ctaButtonText,
  initialIndex,
  cardOrder,
  heroCardCardBenefits,
  staticTopGames,
  games,
  validDate,
  getCurrentIndex,
  JackpotBgImage,
  fsCount,
}: CoverflowSliderTemplateProps) => {
  const { t } = useTranslation('glossary');
  const [copyActive, setCopyActive] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  const [isAnimating, setIsAnimating] = useState(false);
  const { isMobile } = useMedia();
  const { marker } = useIsSticky({ position: 'bottom' });
  const { buttonAction, isLoading } = useButtonAction();

  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const sliderCards: Record<string, CardFactory> = useMemo(
    () => ({
      hero: (isActive) => (
        <HeroCard
          cardBenefits={heroCardCardBenefits}
          fsCount={fsCount}
          heroImage={heroImage}
          isActive={isActive}
          translation={translation}
          validDate={validDate}
        />
      ),
      welcome: () => <WelcomeOfferCard translation={translation} />,
      vip: () => <VIPCard translation={translation} />,
      games: () => <TopGamesCard games={games} staticCard={staticTopGames} />,
      jackpot: () => (
        <JackpotCard bgImage={JackpotBgImage} translation={translation} />
      ),
    }),
    [
      validDate,
      staticTopGames,
      games,
      heroImage,
      translation,
      JackpotBgImage,
      heroCardCardBenefits,
    ]
  );

  const cardConfigs = useMemo(() => {
    const orderedNames: CardName[] = cardOrder ?? [
      'hero',
      'welcome',
      'vip',
      'games',
      'jackpot',
    ];
    return orderedNames.map((name) => sliderCards[name]);
  }, [cardOrder, sliderCards]);

  useWorkSans();

  useEffect(() => {
    getCurrentIndex?.(currentIndex);
  }, [currentIndex, getCurrentIndex]);

  const handleNext = useCallback(() => {
    if (isAnimating) return;

    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % cardConfigs.length);

    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, cardConfigs.length]);

  const handlePrev = useCallback(() => {
    if (isAnimating) return;

    setIsAnimating(true);
    setCurrentIndex(
      (prev) => (prev - 1 + cardConfigs.length) % cardConfigs.length
    );

    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, cardConfigs.length]);

  const handleDotClick = useCallback(
    (index: number) => {
      if (isAnimating || index === currentIndex) return;

      setIsAnimating(true);
      setCurrentIndex(index);

      setTimeout(() => setIsAnimating(false), 500);
    },
    [isAnimating, currentIndex]
  );

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!(touchStartX.current && touchEndX.current)) return;
    const deltaX = touchStartX.current - touchEndX.current;

    const minSwipeDistance = 50;

    if (Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleGoToIndex = useCallback(
    (index: number) => {
      if (isAnimating) return;
      if (index === currentIndex) return;
      setIsAnimating(true);
      setCurrentIndex(index);
      setTimeout(() => setIsAnimating(false), 500);
    },
    [isAnimating, currentIndex]
  );

  return (
    <ScCoverflowSliderTemplate backgroundUrl={backgroundImage}>
      <div className="logo-wrapper">
        <Logo
          logoSize={{
            width: '152',
            height: '40',
            widthMob: '121',
            heightMob: '32',
          }}
        />
      </div>
      <section
        className="content"
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
      >
        <div className="slider">
          <div className="slider-container">
            {cardConfigs.map((renderCard, index) => {
              const position = getCardPosition(
                index,
                cardConfigs.length,
                currentIndex
              );
              const cardStyle = getCardStyle(position, isMobile);
              const isActive = index === currentIndex;

              return (
                <div
                  className={`card ${isAnimating ? 'animating' : ''} ${
                    index === currentIndex ? 'active' : ''
                  }`}
                  key={index}
                  onClick={() => handleGoToIndex(index)}
                  style={{
                    ...cardStyle,
                  }}
                >
                  {renderCard(isActive)}
                </div>
              );
            })}
          </div>
        </div>
        <div className="controls">
          <button
            aria-label="Previous card"
            className="arrow prev"
            disabled={isAnimating}
            onClick={handlePrev}
          >
            <ImageComponent
              alt="arrow"
              height={17}
              src="/lp/dragon-pearls-november-25/arrow-slider.svg"
              width={11}
            />
          </button>

          <div className="dots">
            {cardConfigs.map((_, index) => (
              <button
                aria-label={`Go to card ${index + 1}`}
                className={`dot ${index === currentIndex ? 'active-dot' : ''}`}
                disabled={isAnimating}
                key={index}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>
          <button
            aria-label="Next card"
            className="arrow next"
            disabled={isAnimating}
            onClick={handleNext}
          >
            <ImageComponent
              alt="arrow"
              height={17}
              src="/lp/dragon-pearls-november-25/arrow-slider.svg"
              width={11}
            />
          </button>
        </div>
        <div className="footer">
          {code && (
            <div className="code-wrapper">
              <DashedCopyCode
                borderStyle="dashed"
                buttonColor="goldenOrangeGradient"
                code={code}
                copyActive={copyActive}
                noMarginBottom
                setCopyActive={setCopyActive}
              />
            </div>
          )}
        </div>
        <div className="button-wrapper">
          <Button height="56px" secondaryGreenGradient wide {...buttonAction}>
            {isLoading ? (
              <Loader />
            ) : (
              ctaButtonText || t('common:button.signUpLp')
            )}
          </Button>
        </div>
        {marker}
      </section>
    </ScCoverflowSliderTemplate>
  );
};
